import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Hexagon, Loader2, ArrowLeft, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Berhasil masuk");
      navigate("/admin");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-navy-soft border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan/60 focus:ring-2 focus:ring-cyan/20 transition-all";

  return (
    <div className="min-h-screen grain relative bg-navy text-white grid place-items-center px-4">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] radial-glow z-0" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-navy-card/70 backdrop-blur-xl p-8"
      >
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan transition mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke situs
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <img src="/logo.webp" alt="Binara Labs" className="w-11 h-11 object-contain" />
          <span className="font-heading font-extrabold tracking-tight text-xl">BINARA<span className="text-gold">.</span>LABS</span>
        </div>
        <h1 className="font-heading font-bold text-2xl mt-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-gold" /> Panel Admin
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">Masuk untuk mengelola konten website.</p>

        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-sm text-slate-300 font-medium">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`mt-2 ${inputCls}`} placeholder="admin@binaralabs.id" required data-testid="login-email" />
          </div>
          <div>
            <label className="text-sm text-slate-300 font-medium">Kata Sandi</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`mt-2 ${inputCls}`} placeholder="••••••••" required data-testid="login-password" />
          </div>
          <div className="text-right -mt-1">
            <Link to="/admin/forgot-password" data-testid="forgot-password-link" className="text-xs text-slate-400 hover:text-gold transition">
              Lupa kata sandi?
            </Link>
          </div>
          <button type="submit" disabled={loading} data-testid="login-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3.5 font-body font-bold text-navy hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] disabled:opacity-60 transition-all duration-300">
            {loading && <Loader2 className="w-5 h-5 animate-spin" />} Masuk
          </button>
        </form>
      </motion.div>
    </div>
  );
}
